// src/pages/brand-collaborations-client-stories/components/CollaborationCTA.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CollaborationCTA = () => {
  const partnershipBenefits = [
    {
      icon: 'Handshake',
      title: 'Strategic Partnership',
      description: 'Long-term creative collaboration beyond project-based work'
    },
    {
      icon: 'TrendingUp',
      title: 'Measurable Results',
      description: 'Quantified business impact and performance metrics tracking'
    },
    {
      icon: 'Users',
      title: 'Team Integration',
      description: 'Seamless collaboration with your existing corporate teams'
    },
    {
      icon: 'Award',
      title: 'Excellence Guaranteed',
      description: '99%+ client satisfaction with industry-leading quality standards'
    }
  ];

  const nextSteps = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'Strategic discussion about your vision and objectives',
      duration: '60 minutes'
    },
    {
      step: '02',
      title: 'Partnership Proposal',
      description: 'Customized collaboration framework and project roadmap',
      duration: '2-3 days'
    },
    {
      step: '03',
      title: 'Pilot Project',
      description: 'Small-scale collaboration to demonstrate value and fit',
      duration: '2-4 weeks'
    },
    {
      step: '04',
      title: 'Strategic Partnership',
      description: 'Long-term relationship with dedicated team and resources',
      duration: 'Ongoing'
    }
  ];

  return (
    <section className="py-20 bg-background-dark relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <Image
          src="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=2000"
          alt="Strategic partnership collaboration"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-dark/95 via-background-dark/90 to-background-dark/95"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main CTA Section */}
        <div className="text-center mb-16">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-accent/20 backdrop-blur-sm border border-accent/30 rounded-full px-6 py-2 mb-8">
            <Icon name="Sparkles" size={16} color="#D4AF37" strokeWidth={2} />
            <span className="text-accent font-medium text-sm">Begin Your Success Story</span>
          </div>

          {/* Main Heading */}
          <h2 className="text-4xl md:text-6xl font-bold text-text-primary-inverse mb-6 leading-tight">
            Ready to Create Your
            <span className="text-gradient-gold block">Partnership Story?</span>
          </h2>

          {/* Subheading */}
          <p className="text-xl md:text-2xl text-text-secondary-inverse mb-12 leading-relaxed max-w-4xl mx-auto">
            Join the ranks of Fortune 500 companies and luxury brands who have transformed 
            their visual storytelling through strategic creative partnerships
          </p>

          {/* Primary CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16">
            <Link
              to="/booking-concierge-premium-consultation"
              className="bg-accent hover:bg-accent-bright text-white px-10 py-4 rounded-lg font-bold text-lg cinematic-transition cta-glow hover:shadow-elevation flex items-center space-x-3"
            >
              <Icon name="Calendar" size={20} strokeWidth={2} />
              <span>Schedule Strategic Consultation</span>
            </Link>
            <Link
              to="/cinematic-showcase-portfolio"
              className="border-2 border-text-secondary-inverse text-text-primary-inverse hover:bg-white/10 px-10 py-4 rounded-lg font-bold text-lg cinematic-transition backdrop-blur-sm flex items-center space-x-3"
            >
              <Icon name="Eye" size={20} strokeWidth={2} />
              <span>Explore Success Stories</span>
            </Link>
          </div>
        </div>

        {/* Partnership Benefits */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-text-primary-inverse mb-12 text-center">
            Partnership <span className="text-accent">Advantages</span>
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {partnershipBenefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-accent/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={benefit.icon} size={28} color="#D4AF37" strokeWidth={2} />
                </div>
                <h4 className="text-text-primary-inverse font-bold text-lg mb-2">{benefit.title}</h4>
                <p className="text-text-secondary-inverse text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps Process */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-text-primary-inverse mb-12 text-center">
            Your Partnership <span className="text-accent">Journey</span>
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {nextSteps.map((step, index) => (
              <div key={index} className="relative">
                {/* Step Number */}
                <div className="text-6xl font-bold text-accent/20 mb-4">{step.step}</div>
                <div className="absolute top-0 left-0">
                  <div className="text-2xl font-bold text-accent">{step.step}</div>
                </div>
                
                {/* Step Content */}
                <div className="mt-8">
                  <h4 className="text-text-primary-inverse font-bold text-lg mb-2">{step.title}</h4>
                  <p className="text-text-secondary-inverse text-sm mb-3 leading-relaxed">{step.description}</p>
                  <span className="inline-block bg-accent/20 text-accent px-3 py-1 rounded-full text-xs font-medium">
                    {step.duration}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="grid md:grid-cols-4 gap-8 mb-16">
          <div className="text-center">
            <div className="text-4xl font-bold text-accent mb-2">150+</div>
            <div className="text-text-secondary-inverse text-sm">Brand Partnerships</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-accent mb-2">85%</div>
            <div className="text-text-secondary-inverse text-sm">Repeat Collaborations</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-accent mb-2">5+</div>
            <div className="text-text-secondary-inverse text-sm">Years Average Partnership</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-accent mb-2">99%</div>
            <div className="text-text-secondary-inverse text-sm">Client Satisfaction</div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 max-w-4xl mx-auto">
            <h4 className="text-2xl font-bold text-text-primary-inverse mb-4">
              Transform Your Brand Story Today
            </h4>
            <p className="text-text-secondary-inverse mb-6 leading-relaxed">
              Join industry leaders who have elevated their brand presence through strategic creative partnerships. 
              Your success story begins with a conversation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/booking-concierge-premium-consultation"
                className="bg-accent hover:bg-accent-bright text-white px-8 py-3 rounded-lg font-semibold cinematic-transition flex items-center space-x-2"
              >
                <Icon name="MessageSquare" size={18} strokeWidth={2} />
                <span>Start the Conversation</span>
              </Link>
              <a
                href="tel:+1-555-0123"
                className="border border-text-secondary-inverse text-text-primary-inverse hover:bg-white/10 px-8 py-3 rounded-lg font-semibold cinematic-transition flex items-center space-x-2"
              >
                <Icon name="Phone" size={18} strokeWidth={2} />
                <span>Call Directly</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CollaborationCTA;